// WHIT-485 round 2: the page's English address from its route. For a page
// whose route has a variable part (/holidays/[slug]), router.pathname is the
// route itself, so the language alternates in CustomHead named
// /en/holidays/[slug]/; this fills the parts in from the query.
// Pure (no DOM, no React) so `node --test web/utils/routePath.test.js` can
// load it.

const { translateProductPath } = require('./productPaths');

const DYNAMIC_SEGMENT = /\[(\.\.\.)?([^\]]+)\]/g;

const filledPathname = (pathname, query = {}) =>
  pathname.replace(DYNAMIC_SEGMENT, (segment, rest, name) => {
    const value = query[name];
    if (value === undefined) return segment;
    return (Array.isArray(value) ? value : [value])
      .map(part => encodeURIComponent(String(part)))
      .join('/');
  });

// The query without the route's own parts: once filledPathname has put them
// in the path, leaving them in the query adds ?slug=turkey to the address.
const routeQuery = (pathname, query = {}) => {
  const names = [...pathname.matchAll(DYNAMIC_SEGMENT)].map(match => match[2]);
  return Object.fromEntries(
    Object.entries(query).filter(([name]) => !names.includes(name)),
  );
};

// The page's address in `locale`, for the language alternates: English from
// the route, another language from urlTranslations when the page has an
// address there, and otherwise the address being viewed. Before, every
// non-English alternate was the address being viewed, so an English page
// named /hu/holidays/turkey/ instead of /hu/torokorszag/.
const alternatePath = (locale, enPathname, asPath, rewrites) => {
  // A package-tour page's address depends on the product, which the route
  // does not show: the address being viewed is translated (WHIT-505).
  const productPage = translateProductPath(
    String(asPath || '').split('?')[0],
    locale,
  );
  if (productPage) return `/${locale}${productPage}`;
  const en = enPathname === '/' ? '/' : `${enPathname}/`;
  if (locale === 'en') return `/en${en}`;
  const translated = Object.keys(rewrites).find(
    path =>
      rewrites[path].rewriteUrl === en && rewrites[path].locale === locale,
  );
  return `/${locale}${translated || asPath}`;
};

// Where the language switch goes: the page's address in `locale` and the
// query to keep. As findKeyByRewriteUrl in urlTranslations, a page with no
// address in that language goes to the home page.
const languageSwitchTarget = (pathname, query, locale, rewrites) => {
  const enPathname = filledPathname(pathname, query);
  const target = enPathname === '/' ? '/' : `${enPathname}/`;
  const translated =
    locale === 'en' || target === '/'
      ? target
      : Object.keys(rewrites).find(
          path =>
            rewrites[path].rewriteUrl === target &&
            rewrites[path].locale === locale,
        ) || '/';
  return { pathname: translated, query: routeQuery(pathname, query) };
};

module.exports = {
  filledPathname,
  routeQuery,
  alternatePath,
  languageSwitchTarget,
};
